import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { X, Search, UserPlus } from '../../icons';
import {
  searchInviteCandidates, addTeamMember,
  type CandidateDto,
} from '../../api/project-teams';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';

interface TeamMemberInviteModalProps {
  teamId: string;
  onClose: () => void;
  /** 멤버 추가 발생 시 1회 이상 호출 — caller 가 멤버 목록 재조회. */
  onAdded: () => void;
  isLeaving?: boolean;
}

/**
 * TeamMemberInviteModal (project-menu-detailing) — 기관 내 사용자 검색 → 즉시 추가.
 * Phase 1 즉시추가형 (수락 대기형은 비범위 — ADR). snapshot 정책 안내 문구 포함.
 */
export function TeamMemberInviteModal({ teamId, onClose, onAdded, isLeaving = false }: TeamMemberInviteModalProps) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  const [q, setQ] = useState('');
  const [candidates, setCandidates] = useState<CandidateDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());
  const [addingId, setAddingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEscapeClose(onClose, addingId !== null);

  // 검색 — 300ms debounce. 빈 q 도 후보 노출 (기관 내 미가입 사용자 상위 10).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void (async () => {
        setLoading(true);
        setError(null);
        try {
          const list = await searchInviteCandidates(teamId, q.trim());
          setCandidates(list);
        } catch (err) {
          setError((err as any)?.response?.data?.message ?? (err as Error).message ?? tProject('project.invite.err_search'));
        } finally {
          setLoading(false);
        }
      })();
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [teamId, q]);

  async function handleAdd(userId: string) {
    setAddingId(userId);
    setError(null);
    try {
      await addTeamMember(teamId, { userId });
      setAddedIds((prev) => new Set(prev).add(userId));
      onAdded();
    } catch (err) {
      const code = (err as any)?.response?.data?.code ?? '';
      const msg = (err as any)?.response?.data?.message ?? (err as Error).message;
      setError(`${code ? `[${code}] ` : ''}${msg ?? tProject('project.invite.err')}`);
    } finally {
      setAddingId(null);
    }
  }

  return (
    <div
      className={`pj-modal-backdrop${isLeaving ? ' pj-modal-backdrop--leaving' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget && addingId === null && !isLeaving) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={tProject('project.invite.title')}
      aria-hidden={isLeaving || undefined}
    >
      <div ref={trapRef} className={`pj-modal${isLeaving ? ' pj-modal--leaving' : ''}`}>
        <header className="pj-modal-header">
          <h2>{tProject('project.invite.title')}</h2>
          <button type="button" className="pj-modal-close" onClick={onClose} aria-label={tProject('project.btn.close')}>
            <X size={18} />
          </button>
        </header>

        <div className="pj-modal-body">
          <div className="pj-invite-search">
            <Search size={14} />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={tProject('project.invite.placeholder_search')}
              maxLength={100}
              autoFocus
            />
          </div>

          <p className="pj-invite-note">{tProject('project.invite.note')}</p>

          {error && <div className="pj-modal-error" role="alert">{error}</div>}

          {loading ? (
            <div className="pj-loading" role="status">…</div>
          ) : candidates.length === 0 ? (
            <div className="pj-empty" role="status">{tProject('project.invite.empty')}</div>
          ) : (
            <ul className="pj-invite-list">
              {candidates.map((c) => {
                const added = addedIds.has(c.userId);
                return (
                  <li key={c.userId} className="pj-invite-row">
                    {c.avatarUrl ? (
                      <img className="pj-team-member-avatar" src={c.avatarUrl} alt="" />
                    ) : (
                      <span className="pj-team-member-avatar pj-team-member-avatar--initial" aria-hidden="true">
                        {c.name.slice(0, 1)}
                      </span>
                    )}
                    <span className="pj-invite-info">
                      <span className="pj-invite-name">{c.name}</span>
                      <span className="pj-invite-email">{c.email}</span>
                    </span>
                    <button
                      type="button"
                      className="pj-invite-add"
                      onClick={() => handleAdd(c.userId)}
                      disabled={added || addingId !== null}
                    >
                      <UserPlus size={13} />
                      {added ? tProject('project.invite.added') : tProject('project.invite.add')}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <footer className="pj-modal-footer">
            <button type="button" onClick={onClose}>{tProject('project.btn.close')}</button>
          </footer>
        </div>
      </div>
    </div>
  );
}
